import { Box, Button, Flex, Menu, Skeleton, Stack, Text } from '@chakra-ui/react';
import { DotsThreeVertical, WarningCircle } from '@phosphor-icons/react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AgencyPlan, isMeetingInProgress, MeetingStatus } from '@/types/enums';
import { MeetingSummary } from '@/types/Meeting';
import RecordingIndicator from '@/components/media/RecordingIndicator';
import DeleteDialog from '@/components/dialogs/DeleteDialog';
import { fetchAPI } from '@/api/fetch';
import { apiDeleteMeeting } from '@/api/services/meeting.service';

import AddInterviewDialog from './AddInterviewDialog';
import UpdateMeetingLinkDialog from './UpdateMeetingLinkDialog';

interface UpcomingInterviewCardProps {
  meeting: MeetingSummary;
  onDeleteSuccess: (meetingId: string) => void;
  onEditSuccess: (meeting: MeetingSummary) => void;
  isLoading?: boolean;
  agencyPlan: AgencyPlan;
}

const formatTime = (date: Date): string => {
  return date.toLocaleTimeString('default', {
    hour: 'numeric',
    minute: 'numeric'
  });
};

export default function UpcomingInterviewCard({
  meeting,
  onDeleteSuccess,
  onEditSuccess,
  isLoading = false,
  agencyPlan
}: UpcomingInterviewCardProps) {
  const navigate = useNavigate();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isUpdateLinkDialogOpen, setIsUpdateLinkDialogOpen] = useState(false);
  const isFatal = meeting.status === MeetingStatus.Fatal;

  const handleDeleteConfirm = async () => {
    const response = await fetchAPI<void>({
      request: () => apiDeleteMeeting(meeting.meeting_id)
    });

    if (response.success) {
      onDeleteSuccess(meeting.meeting_id);
    } else {
      throw new Error(response.message || 'Failed to delete interview');
    }
  };

  const handleCardClick = () => {
    if (isFatal) {
      setIsUpdateLinkDialogOpen(true);
    } else {
      navigate(`/interview/${meeting.meeting_id}`);
    }
  };

  if (isLoading) {
    return (
      <Box
        w="100%"
        bg="neutrals.50"
        border="1px solid"
        borderColor="neutrals.100"
        borderRadius="12px"
        p="24px"
      >
        <Flex justifyContent="space-between" align="center" w="100%">
          <Stack gap="4px">
            <Skeleton height="20px" width="180px" />
            <Skeleton height="24px" width="220px" />
          </Stack>
          <Skeleton height="36px" width="80px" borderRadius="8px" />
        </Flex>
      </Box>
    );
  }

  return (
    <>
      <Box
        w="100%"
        bg={isFatal ? 'error.100' : 'neutrals.50'}
        border="1px solid"
        borderColor={isFatal ? 'error.300' : 'neutrals.100'}
        borderRadius="12px"
        py="24px"
        pl="24px"
        pr="12px"
        title={meeting.title}
        onClick={(e) => {
          e.stopPropagation();
          handleCardClick();
        }}
        cursor="pointer"
        _hover={{ cursor: 'pointer', boxShadow: 'sm' }}
        transition="all 0.2s ease-in-out"
      >
        <Flex justifyContent="space-between" align="center" w="100%" gap="4px">
          <Stack gap="4px">
            <Flex align="center" gap="12px">
              {isFatal && <WarningCircle size={20} color="#D92D20" />}
              <Text fontSize="14px" color="neutrals.700">
                {formatTime(new Date(meeting.meeting_start_time * 1000))} -{' '}
                {formatTime(new Date(meeting.meeting_end_time * 1000))}
              </Text>
              {isMeetingInProgress(meeting.status) && (
                <RecordingIndicator size="12px" aria-label="Recording in progress" />
              )}
            </Flex>
            <Text
              fontSize="18px"
              color="neutrals.900"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              maxWidth="260px"
              fontWeight="semibold"
            >
              {meeting.title}
            </Text>
          </Stack>
          <Menu.Root>
            <Menu.Trigger asChild>
              <Button
                variant="ghost"
                w="fit-content"
                h="fit-content"
                p="0"
                borderRadius="full"
                color="neutrals.900"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                }}
                _hover={{
                  bg: isFatal ? 'error.300' : 'neutrals.100'
                }}
                aria-label={`View options for ${meeting.title}`}
              >
                <DotsThreeVertical />
              </Button>
            </Menu.Trigger>
            <Menu.Positioner>
              <Menu.Content>
                {!isFatal && (
                  <Menu.Item
                    value="edit"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsEditDialogOpen(true);
                    }}
                    cursor="pointer"
                    fontSize="14px"
                    p="8px 12px"
                    aria-label="Edit interview"
                  >
                    Edit
                  </Menu.Item>
                )}
                <Menu.Item
                  value="delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsDeleteDialogOpen(true);
                  }}
                  cursor="pointer"
                  fontSize="14px"
                  p="8px 12px"
                  aria-label="Delete interview"
                >
                  Delete
                </Menu.Item>
              </Menu.Content>
            </Menu.Positioner>
          </Menu.Root>
        </Flex>
      </Box>

      <AddInterviewDialog
        mode="edit"
        meeting={meeting}
        onEditInterview={onEditSuccess}
        isOpen={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        plan={agencyPlan}
      />

      <DeleteDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        itemName={meeting.title}
        itemType="interview"
        onConfirm={handleDeleteConfirm}
      />

      <UpdateMeetingLinkDialog
        meeting={meeting}
        onEditMeeting={onEditSuccess}
        isOpen={isUpdateLinkDialogOpen}
        onClose={() => setIsUpdateLinkDialogOpen(false)}
      />
    </>
  );
}
